/*!
 * rr-announce.js: behaviour layer for the rotating announcement bar
 * (Raptor Racing brand spec 6A, owner-approved v2.12, reference
 * search-and-announcement-concepts.html; 27 Sept 2026).
 *
 * Loaded from layout/theme.liquid with defer, after rr-motion.js:
 *   <script src="{{ 'rr-announce.js' | asset_url }}" defer></script>
 * Markup and CSS: sections/announcement-bar.liquid. ES5, no jQuery. The
 * whole file runs inside try/catch and every handler is guarded, so a
 * failure here leaves the server-rendered first slide in place.
 *
 * Hooks, in the order they run:
 *  0. BOOT on DOMContentLoaded (at once if the document is already parsed).
 *     Every [data-rr-ann] becomes a rotator. Re-run on shopify:section:load,
 *     torn down on shopify:section:unload.
 *  1. DISMISS: sessionStorage rr-ann-dismissed = '1'. At boot with the key
 *     set, the section wrapper (#shopify-section-announcement_bar) gets the
 *     hidden attribute at once (the section also carries an inline guard
 *     that does the same before first paint). The dismiss button sets the
 *     key, calls window.PXUTheme.jsAnnouncementBar.disableSticky when it
 *     exists (unsticks the theme's wrapper and resets the header offset) and
 *     hides the wrapper; rr-motion.js's observer sees the hidden attribute
 *     and re-measures --rr-announcement-h. Ignored inside the theme editor
 *     so the owner always sees the bar there.
 *  2. ROTATION: every data-interval ms (minimum 3000) the active slide gets
 *     .is-out (removed after 460 ms) and the next gets .is-active; the dots
 *     follow. The 2px timer line restarts by dropping and re-adding
 *     .is-running with a forced reflow. Hover and keyboard focus (focusin
 *     whose target matches :focus-visible; a mouse click on an arrow focuses
 *     the button in Chrome, Edge and Firefox but must not hold the bar) put
 *     .is-paused on the bar (CSS pauses the line) and clear the timeout;
 *     leaving resumes with the remaining time read from the line's width,
 *     as the mock does. The pause button toggles aria-pressed, aria-label
 *     and its glyph (CSS) and stops or restarts the cycle. Prev and next
 *     restart the cycle, they never stop it. Left and right arrow keys
 *     inside the bar act as prev and next. prefers-reduced-motion: no
 *     auto-advance, arrows still work (the CSS hides the line).
 *     shopify:block:select shows that slide and holds it until deselect.
 *  3. CART: when a [data-rr-ann-fs] slide is on the page, /cart.js is read
 *     again after gv:cart:added, after window.PXUTheme.jsCart.updateView
 *     (wrapped; the theme calls it with the /cart/change.js response) and
 *     700 ms after any change or click inside the cart form. Shipping
 *     Protection lines are subtracted the way cart__main.liquid does; the
 *     slide's copy, amount (window.PXUTheme.currency.money_format through
 *     Shopify.formatMoney, else the body data-money-format), progress bar
 *     and state class update in place.
 *  5. SCHEDULE (spec v2.18 6A, 28 Sept 2026): slides with data-rr-sched
 *     carry data-start / data-end ("YYYY-MM-DD HH:MM", Toronto time). At
 *     boot, every 15 s and when the tab comes back, each rotator re-picks
 *     its live set in DOM order (the Liquid already put priority and
 *     scheduled slides first): a scheduled slide is live from its start to
 *     the end of its end minute; of the live sales only the one ending
 *     first stays; the first three live slides get .is-on, the rest .is-off.
 *     Sale end text is rebuilt from the Toronto clock each pass ("Ends
 *     Tuesday, Sep 29.", "Ends tonight at midnight." on the last day; an
 *     end at 00:00 makes the day before the last day). The Liquid renders
 *     the same answer for the first paint; this pass only corrects pages
 *     Shopify served from cache.
 *  4. MEMBER: when a [data-rr-ann-member] slide is on the page, poll for
 *     window.rrSmile (assets/rr-smile.js) every 250 ms for up to 8 s, call
 *     getBalance and, only for a non-negative integer, swap in the balance
 *     template and link held in the slide's data attributes. null, absent
 *     or anything else: the fallback copy stays.
 */
(function () {
  'use strict';
  try {
    var win = window;
    var doc = document;
    var KEY = 'rr-ann-dismissed';
    var OUT_MS = 460;
    var MIN_INTERVAL = 3000;
    var DEFAULT_INTERVAL = 5000;
    var CART_DELAY = 700;
    var SMILE_STEP = 250;
    var SMILE_LIMIT = 8000;

    var reduced = false;
    try {
      reduced = !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (err) {
      reduced = false;
    }
    var designMode = !!(win.Shopify && win.Shopify.designMode);
    var rotators = [];

    /* ------------------------------------------------------------------------
       Helpers
       ------------------------------------------------------------------------ */
    function warn(err) {
      if (win.console && typeof win.console.warn === 'function') {
        win.console.warn('rr-announce: ' + (err && err.message ? err.message : err));
      }
    }

    function guard(fn) {
      return function () {
        try {
          return fn.apply(this, arguments);
        } catch (err) {
          warn(err);
        }
      };
    }

    function toArray(list) {
      var out = [];
      for (var i = 0; i < list.length; i++) out.push(list[i]);
      return out;
    }

    function matches(el, selector) {
      var fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
      return fn ? fn.call(el, selector) : false;
    }

    function closest(el, selector) {
      while (el && el.nodeType === 1) {
        if (matches(el, selector)) return el;
        el = el.parentNode;
      }
      return null;
    }

    function contains(root, el) {
      return !!(root && el && (root === el || (root.contains && root.contains(el))));
    }

    function isDismissed() {
      try {
        return win.sessionStorage.getItem(KEY) === '1';
      } catch (err) {
        return false;
      }
    }

    function setDismissed() {
      try {
        win.sessionStorage.setItem(KEY, '1');
      } catch (err) {
        /* private mode or storage blocked: the bar hides for this page view only */
      }
    }

    function sectionOf(root) {
      return closest(root, '.shopify-section') || doc.getElementById('shopify-section-announcement_bar');
    }

    function stripTags(s) {
      return String(s).replace(/<[^>]*>/g, '');
    }

    /* Lead phrase rule shared with the Liquid: the first sentence (up to the
       first ". ") is the bold white lead, the rest is message grey; text with
       no sentence break is all lead. */
    function leadSplit(text) {
      text = String(text || '').replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
      var at = text.indexOf('. ');
      if (at < 0) return { lead: text, rest: '' };
      return { lead: text.slice(0, at + 1), rest: text.slice(at + 2) };
    }

    function renderLead(el, text) {
      if (!el) return;
      while (el.firstChild) el.removeChild(el.firstChild);
      var parts = leadSplit(text);
      var strong = doc.createElement('strong');
      strong.appendChild(doc.createTextNode(parts.lead));
      el.appendChild(strong);
      if (parts.rest) el.appendChild(doc.createTextNode(' ' + parts.rest));
    }

    function hideSection(root) {
      var section = sectionOf(root);
      if (section) section.setAttribute('hidden', '');
      root.setAttribute('hidden', '');
    }

    /* ------------------------------------------------------------------------
       Schedule helpers (Toronto clock)
       ------------------------------------------------------------------------ */
    var TZ = 'America/Toronto';
    var DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    var tzFmt = null;
    try {
      tzFmt = new Intl.DateTimeFormat('en-US', { timeZone: TZ, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' });
    } catch (err) {
      tzFmt = null;
    }

    /* Toronto wall-clock parts of an instant */
    function torontoParts(ms) {
      if (tzFmt) {
        var p = {};
        var list = tzFmt.formatToParts(new Date(ms));
        for (var i = 0; i < list.length; i++) p[list[i].type] = list[i].value;
        return { y: +p.year, m: +p.month, d: +p.day, h: (+p.hour) % 24, mi: +p.minute };
      }
      var t = new Date(ms - 4 * 3600000); /* no Intl: assume EDT */
      return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate(), h: t.getUTCHours(), mi: t.getUTCMinutes() };
    }

    /* "YYYY-MM-DD HH:MM" Toronto time to an instant (ms), or null */
    function torontoEpoch(str) {
      var m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/.exec(String(str || '').replace(/^\s+/, ''));
      if (!m) return null;
      var want = Date.UTC(+m[1], +m[2] - 1, +m[3], m[4] ? +m[4] : 0, m[5] ? +m[5] : 0);
      var guess = want + 4 * 3600000;
      for (var k = 0; k < 2; k++) {
        var p = torontoParts(guess);
        guess += want - Date.UTC(p.y, p.m - 1, p.d, p.h, p.mi);
      }
      return guess;
    }

    function schedWindow(slide) {
      var s = torontoEpoch(slide.getAttribute('data-start'));
      var endRaw = slide.getAttribute('data-end') || '';
      var e = torontoEpoch(endRaw);
      var t = /(\d{1,2}):(\d{2})\s*$/.exec(endRaw);
      return {
        start: s === null ? -Infinity : s,
        end: e === null ? Infinity : e + 60000, /* through the end minute */
        endAt: e,
        endH: t ? +t[1] : null,
        endMi: t ? +t[2] : null
      };
    }

    function clock(h, mi) {
      var h12 = h % 12 || 12;
      return h12 + (mi ? ':' + (mi < 10 ? '0' : '') + mi : '') + (h < 12 ? 'am' : 'pm');
    }

    /* Rebuild a sale slide's text from the live Toronto clock */
    function renderSale(slide, w) {
      var box = slide.querySelector('[data-rr-ann-text]');
      if (!box || w.endAt === null) return;
      var midnight = (w.endH === 0 && w.endMi === 0) || (w.endH === 23 && w.endMi === 59);
      var lastAt = w.endH === 0 && w.endMi === 0 ? w.endAt - 60000 : w.endAt;
      var last = torontoParts(lastAt);
      var today = torontoParts(Date.now());
      var isLast = last.y === today.y && last.m === today.m && last.d === today.d;
      var wd = DAYS[new Date(Date.UTC(last.y, last.m - 1, last.d, 12)).getUTCDay()];
      var lead = slide.getAttribute('data-lead') || '';
      var body = slide.getAttribute('data-body') || '';
      var at = midnight ? 'at midnight' : 'at ' + clock(w.endH, w.endMi);
      var bold;
      var rest;
      if (slide.getAttribute('data-variant') === 'discount') {
        bold = lead + ' ends ' + (isLast ? 'tonight' : wd) + ' ' + at + '.';
        rest = 'Prices shown include the discount.';
      } else {
        bold = lead + (body ? ': ' + body : '') + '.';
        if (isLast) rest = midnight ? 'Ends tonight at midnight.' : 'Ends today ' + at + '.';
        else rest = 'Ends ' + wd + ', ' + MONTHS[last.m - 1] + ' ' + last.d + (midnight ? '' : ' ' + at) + '.';
      }
      if (box.getAttribute('data-rr-built') === bold + '|' + rest) return;
      while (box.firstChild) box.removeChild(box.firstChild);
      var strong = doc.createElement('strong');
      strong.appendChild(doc.createTextNode(bold));
      box.appendChild(strong);
      box.appendChild(doc.createTextNode(' ' + rest));
      box.setAttribute('data-rr-built', bold + '|' + rest);
    }

    /* ------------------------------------------------------------------------
       Rotator: one per [data-rr-ann]
       ------------------------------------------------------------------------ */
    function Rotator(root) {
      this.root = root;
      this.section = sectionOf(root);
      this.all = toArray(root.querySelectorAll('[data-rr-ann-slide]'));
      this.slides = [];
      for (var s0 = 0; s0 < this.all.length; s0++) {
        if (!this.all[s0].classList.contains('is-off')) this.slides.push(this.all[s0]);
      }
      this.dots = toArray(root.querySelectorAll('[data-rr-ann-dot]'));
      this.timer = root.querySelector('[data-rr-ann-timer]');
      this.prevBtn = root.querySelector('[data-rr-ann-prev]');
      this.nextBtn = root.querySelector('[data-rr-ann-next]');
      this.pauseBtn = root.querySelector('[data-rr-ann-pause]');
      this.dismissBtn = root.querySelector('[data-rr-ann-dismiss]');
      this.interval = Math.max(MIN_INTERVAL, parseInt(root.getAttribute('data-interval'), 10) || DEFAULT_INTERVAL);
      this.index = 0;
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].classList.contains('is-active')) {
          this.index = i;
          break;
        }
      }
      if (this.slides.length && !this.slides[this.index].classList.contains('is-active')) {
        this.slides[this.index].classList.add('is-active');
      }
      this.timeout = null;
      this.stopped = false;   /* pause button */
      this.hover = false;     /* pointer over the bar */
      this.focus = false;     /* keyboard focus inside the bar */
      this.selected = false;  /* theme editor block selected */
      this.dead = false;
      this.handlers = [];
      this.bind();
      this.updateDots();
      this.restart();
      this.refresh();
    }

    /* 5. SCHEDULE: re-pick the live set with the browser's clock */
    Rotator.prototype.refresh = function () {
      if (this.dead) return;
      var now = Date.now();
      var pick = [];
      var bestSale = null;
      var bestEnd = Infinity;
      var i;
      var slide;
      for (i = 0; i < this.all.length; i++) {
        slide = this.all[i];
        if (!slide.hasAttribute('data-rr-sched')) continue;
        var win2 = schedWindow(slide);
        slide.rrLive = now >= win2.start && now < win2.end;
        if (slide.rrLive && slide.getAttribute('data-kind') === 'sale') {
          renderSale(slide, win2);
          if (win2.end < bestEnd) {
            bestEnd = win2.end;
            bestSale = slide;
          }
        }
      }
      for (i = 0; i < this.all.length && pick.length < 3; i++) {
        slide = this.all[i];
        if (slide.hasAttribute('data-rr-sched')) {
          if (!slide.rrLive) continue;
          if (slide.getAttribute('data-kind') === 'sale' && slide !== bestSale) continue;
        }
        pick.push(slide);
      }
      var same = pick.length === this.slides.length;
      for (i = 0; same && i < pick.length; i++) {
        if (pick[i] !== this.slides[i]) same = false;
      }
      if (same) return;
      var keep = this.slides[this.index];
      for (i = 0; i < this.all.length; i++) {
        slide = this.all[i];
        var on = pick.indexOf(slide) !== -1;
        slide.classList.toggle('is-on', on);
        slide.classList.toggle('is-off', !on);
        slide.classList.remove('is-out');
        if (!on) slide.classList.remove('is-active');
      }
      this.slides = pick;
      var at = pick.indexOf(keep);
      /* a newly live scheduled slide goes first and is shown at once */
      if (pick.length && pick[0] !== keep && pick[0].hasAttribute('data-rr-sched')) at = 0;
      this.index = at < 0 ? 0 : at;
      for (i = 0; i < pick.length; i++) pick[i].classList.toggle('is-active', i === this.index);
      for (i = 0; i < this.dots.length; i++) this.dots[i].classList.toggle('is-off', i >= pick.length);
      this.root.classList.toggle('is-single', pick.length < 2);
      this.root.setAttribute('data-count', String(pick.length));
      this.updateDots();
      this.restart();
    };

    Rotator.prototype.on = function (el, type, fn) {
      if (!el) return;
      var handler = guard(fn);
      /* touch listeners never call preventDefault, so they are passive: the
         browser can start a scroll on the bar without waiting for them */
      el.addEventListener(type, handler, type.indexOf('touch') === 0 ? { passive: true } : false);
      this.handlers.push([el, type, handler]);
    };

    Rotator.prototype.bind = function () {
      var self = this;
      var root = this.root;
      this.on(this.prevBtn, 'click', function () { self.go(self.index - 1); });
      this.on(this.nextBtn, 'click', function () { self.go(self.index + 1); });
      this.on(this.pauseBtn, 'click', function () { self.toggle(); });
      this.on(this.dismissBtn, 'click', function () { self.dismiss(); });
      /* Phones (3 Oct 2026, owner option B): no arrows or pause button, so
         a swipe changes the message and a held touch pauses it. The emulated
         mouseenter a browser fires right after a tap is ignored, or the hover
         hold would keep the bar paused until the next tap elsewhere. */
      var touchX = null, touchY = null, lastTouch = 0;
      this.on(root, 'touchstart', function (event) {
        var t = event.touches && event.touches[0];
        if (!t) return;
        touchX = t.clientX; touchY = t.clientY;
        self.hover = true; self.hold();
      });
      this.on(root, 'touchend', function (event) {
        var t = event.changedTouches && event.changedTouches[0];
        lastTouch = Date.now();
        if (t && touchX !== null) {
          var dx = t.clientX - touchX, dy = t.clientY - touchY;
          if (Math.abs(dx) > 35 && Math.abs(dx) > Math.abs(dy) * 1.2) self.go(self.index + (dx < 0 ? 1 : -1));
        }
        touchX = touchY = null;
        self.hover = false; self.release();
      });
      this.on(root, 'touchcancel', function () {
        touchX = touchY = null; lastTouch = Date.now();
        self.hover = false; self.release();
      });
      this.on(root, 'mouseenter', function () { if (Date.now() - lastTouch < 800) return; self.hover = true; self.hold(); });
      this.on(root, 'mouseleave', function () { self.hover = false; self.release(); });
      /* Only keyboard-driven focus holds the bar (27 Sept 2026 fix). Chrome,
         Edge and Firefox focus a <button> on mouse click, so a plain click on
         prev or next used to set the focus hold and mouseleave could never
         release it: the bar sat paused until the user clicked elsewhere.
         :focus-visible is false for a mouse click and true for Tab, so the
         hold now follows the keyboard only. A browser whose matches() throws
         on :focus-visible keeps the old behaviour (every focus holds). */
      this.on(root, 'focusin', function (event) {
        var keyboard = true;
        try {
          keyboard = matches(event.target, ':focus-visible');
        } catch (err) {
          keyboard = true;
        }
        if (!keyboard) return;
        self.focus = true;
        self.hold();
      });
      this.on(root, 'focusout', function (event) {
        if (event.relatedTarget && contains(root, event.relatedTarget)) return;
        self.focus = false;
        self.release();
      });
      this.on(root, 'keydown', function (event) {
        var key = event.key || event.keyCode;
        if (key === 'ArrowLeft' || key === 37) {
          event.preventDefault();
          self.go(self.index - 1);
        } else if (key === 'ArrowRight' || key === 39) {
          event.preventDefault();
          self.go(self.index + 1);
        }
      });
    };

    Rotator.prototype.held = function () {
      return this.hover || this.focus || this.selected;
    };

    /* Fraction of the interval already shown, read from the timer line. */
    Rotator.prototype.ratio = function () {
      try {
        if (!this.timer) return 0;
        var total = this.root.getBoundingClientRect().width;
        if (!total) return 0;
        var ratio = this.timer.getBoundingClientRect().width / total;
        return Math.max(0, Math.min(1, ratio));
      } catch (err) {
        return 0;
      }
    };

    /* 29 Sept 2026 (Lighthouse): each new message is a first paint, so on a
       page nobody touches every rotation became a later "largest contentful
       paint" candidate (38s on the phone product page). The rotation starts
       with the shopper's first scroll, tap or key: the moment the browser
       stops measuring LCP. Until then the first message stays. */
    var engaged = false;
    var waiting = [];
    var ENGAGE = ['scroll', 'wheel', 'pointerdown', 'keydown', 'touchstart'];
    function engage() {
      if (engaged) return;
      engaged = true;
      for (var e = 0; e < ENGAGE.length; e++) win.removeEventListener(ENGAGE[e], engage, true);
      var list = waiting;
      waiting = [];
      for (var w = 0; w < list.length; w++) { try { list[w].restart(); } catch (err) { /* one bar */ } }
    }
    for (var en = 0; en < ENGAGE.length; en++) win.addEventListener(ENGAGE[en], engage, { passive: true, capture: true });

    Rotator.prototype.schedule = function (ms) {
      var self = this;
      win.clearTimeout(this.timeout);
      this.timeout = win.setTimeout(guard(function () {
        self.timeout = null;
        self.go(self.index + 1);
      }), ms);
    };

    Rotator.prototype.restart = function () {
      win.clearTimeout(this.timeout);
      this.timeout = null;
      if (this.timer) {
        this.timer.classList.remove('is-running');
        void this.timer.offsetWidth; /* forced reflow so the keyframe starts from 0 */
      }
      if (this.dead || this.stopped || reduced || this.slides.length < 2) return;
      if (!engaged) { if (waiting.indexOf(this) < 0) waiting.push(this); return; }
      if (this.timer) this.timer.classList.add('is-running');
      if (this.held()) return; /* .is-paused keeps the line at 0 until release */
      this.schedule(this.interval);
    };

    Rotator.prototype.hold = function () {
      win.clearTimeout(this.timeout);
      this.timeout = null;
      this.root.classList.add('is-paused');
    };

    Rotator.prototype.release = function () {
      if (this.held()) return;
      this.root.classList.remove('is-paused');
      if (this.dead || this.stopped || reduced || this.slides.length < 2) return;
      if (this.timer && this.timer.classList.contains('is-running')) {
        this.schedule(Math.max(50, this.interval * (1 - this.ratio())));
      } else {
        this.restart();
      }
    };

    Rotator.prototype.go = function (n) {
      var len = this.slides.length;
      if (len < 2 || this.dead) return;
      var current = this.slides[this.index];
      current.classList.remove('is-active');
      current.classList.add('is-out');
      win.setTimeout(function () { current.classList.remove('is-out'); }, OUT_MS);
      this.index = ((n % len) + len) % len;
      this.slides[this.index].classList.add('is-active');
      this.updateDots();
      this.restart();
    };

    Rotator.prototype.updateDots = function () {
      for (var i = 0; i < this.dots.length; i++) {
        if (i === this.index) this.dots[i].classList.add('is-active');
        else this.dots[i].classList.remove('is-active');
      }
    };

    Rotator.prototype.toggle = function () {
      this.stopped = !this.stopped;
      var btn = this.pauseBtn;
      if (btn) {
        btn.setAttribute('aria-pressed', this.stopped ? 'true' : 'false');
        var label = this.stopped ? btn.getAttribute('data-label-play') : btn.getAttribute('data-label-pause');
        if (label) btn.setAttribute('aria-label', label);
      }
      if (this.stopped) this.root.classList.add('is-stopped');
      else this.root.classList.remove('is-stopped');
      this.restart();
    };

    Rotator.prototype.dismiss = function () {
      setDismissed();
      this.hide();
    };

    Rotator.prototype.hide = function () {
      this.teardown();
      try {
        var theme = win.PXUTheme;
        if (theme && theme.jsAnnouncementBar && typeof theme.jsAnnouncementBar.disableSticky === 'function') {
          theme.jsAnnouncementBar.disableSticky();
        }
      } catch (err) {
        warn(err);
      }
      hideSection(this.root);
    };

    Rotator.prototype.teardown = function () {
      this.dead = true;
      win.clearTimeout(this.timeout);
      this.timeout = null;
      if (this.timer) this.timer.classList.remove('is-running');
      for (var i = 0; i < this.handlers.length; i++) {
        var h = this.handlers[i];
        h[0].removeEventListener(h[1], h[2]);
      }
      this.handlers = [];
      this.root.rrAnn = null;
    };

    function rotatorFor(el) {
      for (var i = 0; i < rotators.length; i++) {
        if (contains(rotators[i].root, el)) return rotators[i];
      }
      return null;
    }

    /* ------------------------------------------------------------------------
       Cart: live free-shipping slide
       ------------------------------------------------------------------------ */
    var cartBound = false;
    var cartTimer = null;

    function cartTotal(cart) {
      var total = Number(cart && cart.total_price) || 0;
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) {
        var item = items[i] || {};
        var type = String(item.product_type || '');
        var handle = String(item.handle || '');
        if (type === 'Shipping Protection' || handle.indexOf('shipping-protection') !== -1) {
          var line = item.final_line_price;
          if (line === undefined || line === null) line = item.line_price;
          total -= Number(line) || 0;
        }
      }
      return Math.max(0, Math.round(total));
    }

    function moneyFormat() {
      var theme = win.PXUTheme;
      var format = theme && theme.currency && theme.currency.money_format;
      if (!format && doc.body) format = doc.body.getAttribute('data-money-format');
      return format || '${{amount}}';
    }

    function money(cents) {
      var format = moneyFormat();
      if (win.Shopify && typeof win.Shopify.formatMoney === 'function') {
        try {
          return stripTags(win.Shopify.formatMoney(cents, format));
        } catch (err) {
          warn(err);
        }
      }
      var amount = (Math.round(cents) / 100).toFixed(2);
      var out = format.replace(/\[amount\]|\{\{\s*amount[^}]*\}\}/, amount);
      return stripTags(out === format ? '$' + amount : out);
    }

    function applyCart(cart) {
      var slides = doc.querySelectorAll('[data-rr-ann-fs]');
      if (!slides.length) return;
      var total = cartTotal(cart);
      for (var i = 0; i < slides.length; i++) {
        var slide = slides[i];
        var threshold = parseInt(slide.getAttribute('data-threshold'), 10);
        if (!(threshold > 0)) continue;
        var state;
        var text;
        if (total <= 0) {
          state = 'empty';
          text = slide.getAttribute('data-copy-empty') || '';
        } else if (total >= threshold) {
          state = 'unlocked';
          text = slide.getAttribute('data-copy-unlocked') || '';
        } else {
          state = 'away';
          text = String(slide.getAttribute('data-copy-away') || '').replace(/\[amount\]|\{\{\s*amount\s*\}\}/g, money(threshold - total));
        }
        slide.setAttribute('data-total', String(total));
        slide.classList.remove('is-empty');
        slide.classList.remove('is-unlocked');
        slide.classList.remove('is-away');
        slide.classList.add('is-' + state);
        var fill = slide.querySelector('[data-rr-ann-fill]');
        if (fill) fill.style.transform = 'scaleX(' + Math.min(1, total / threshold).toFixed(3) + ')';
        renderLead(slide.querySelector('[data-rr-ann-text]'), text);
      }
    }

    function fetchCart() {
      var xhr = new win.XMLHttpRequest();
      xhr.open('GET', '/cart.js', true);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.onreadystatechange = guard(function () {
        if (xhr.readyState !== 4) return;
        if (xhr.status < 200 || xhr.status > 299) return;
        applyCart(JSON.parse(xhr.responseText));
      });
      xhr.send();
    }

    function scheduleCart() {
      win.clearTimeout(cartTimer);
      cartTimer = win.setTimeout(guard(fetchCart), CART_DELAY);
    }

    function wrapUpdateView() {
      var theme = win.PXUTheme;
      var cartApi = theme && theme.jsCart;
      if (!cartApi || typeof cartApi.updateView !== 'function' || cartApi.updateView.rrWrapped) return;
      var original = cartApi.updateView;
      var wrapped = function (cart) {
        var result = original.apply(this, arguments);
        try {
          if (cart && typeof cart.total_price === 'number') applyCart(cart);
          else scheduleCart();
        } catch (err) {
          warn(err);
        }
        return result;
      };
      wrapped.rrWrapped = true;
      cartApi.updateView = wrapped;
    }

    function inCartForm(el) {
      return !!closest(el, '#cart_form, .cart__form, [data-cart-page-delete]');
    }

    function initCart() {
      if (!doc.querySelector('[data-rr-ann-fs]')) return;
      wrapUpdateView();
      if (cartBound) return;
      cartBound = true;
      doc.addEventListener('gv:cart:added', guard(scheduleCart));
      doc.addEventListener('change', guard(function (event) {
        if (inCartForm(event.target)) scheduleCart();
      }));
      doc.addEventListener('click', guard(function (event) {
        if (inCartForm(event.target)) scheduleCart();
      }));
      win.addEventListener('load', guard(wrapUpdateView));
    }

    /* ------------------------------------------------------------------------
       Member: Dino Dollars balance from rr-smile.js
       ------------------------------------------------------------------------ */
    var knownBalance = null;
    var memberPolling = false;

    function isBalance(value) {
      return typeof value === 'number' && isFinite(value) && Math.floor(value) === value && value >= 0;
    }

    function formatInt(n) {
      return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    function applyBalance(slide, balance) {
      var template = slide.getAttribute('data-rr-member-balance');
      if (!template) return;
      renderLead(slide.querySelector('[data-rr-ann-text]'), template.replace(/\[balance\]|\{\{\s*balance\s*\}\}/g, formatInt(balance)));
      var link = slide.querySelector('[data-rr-ann-link]');
      var label = slide.getAttribute('data-rr-member-link-label');
      var href = slide.getAttribute('data-rr-member-link');
      if (link) {
        if (label) link.textContent = label;
        if (href) link.setAttribute('href', href);
      }
      slide.classList.remove('is-fallback');
      slide.classList.add('is-balance');
    }

    function applyKnownBalance() {
      if (!isBalance(knownBalance)) return;
      var slides = doc.querySelectorAll('[data-rr-ann-member]');
      for (var i = 0; i < slides.length; i++) applyBalance(slides[i], knownBalance);
    }

    function initMember() {
      if (!doc.querySelector('[data-rr-ann-member]')) return;
      if (isBalance(knownBalance)) {
        applyKnownBalance();
        return;
      }
      if (memberPolling) return;
      memberPolling = true;
      var waited = 0;
      var poll = guard(function () {
        var api = win.rrSmile;
        if (api && typeof api.getBalance === 'function') {
          memberPolling = false;
          api.getBalance(guard(function (balance) {
            if (!isBalance(balance)) return;
            knownBalance = balance;
            applyKnownBalance();
          }));
          return;
        }
        waited += SMILE_STEP;
        if (waited >= SMILE_LIMIT) {
          memberPolling = false;
          return;
        }
        win.setTimeout(poll, SMILE_STEP);
      });
      poll();
    }

    /* ------------------------------------------------------------------------
       Boot
       ------------------------------------------------------------------------ */
    function init(scope) {
      var base = scope && scope.nodeType === 1 ? scope : doc;
      var roots = toArray(base.querySelectorAll('[data-rr-ann]'));
      if (base !== doc && matches(base, '[data-rr-ann]')) roots.unshift(base);
      for (var i = 0; i < roots.length; i++) {
        var root = roots[i];
        if (root.rrAnn) continue;
        if (isDismissed() && !designMode) {
          hideSection(root);
          continue;
        }
        root.rrAnn = new Rotator(root);
        rotators.push(root.rrAnn);
      }
      initCart();
      initMember();
    }

    function ready(fn) {
      if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', guard(fn));
      else guard(fn)();
    }

    ready(function () {
      init(doc);
    });

    function refreshAll() {
      for (var i = 0; i < rotators.length; i++) rotators[i].refresh();
    }
    win.setInterval(guard(refreshAll), 15000);
    doc.addEventListener('visibilitychange', guard(function () {
      if (!doc.hidden) refreshAll();
    }));

    /* Theme editor: sections re-render without running inline scripts */
    doc.addEventListener('shopify:section:load', guard(function (event) {
      init(event && event.target);
    }));

    doc.addEventListener('shopify:section:unload', guard(function (event) {
      var target = event && event.target;
      for (var i = rotators.length - 1; i >= 0; i--) {
        var root = rotators[i].root;
        if (contains(target, root) || !contains(doc.documentElement, root)) {
          rotators[i].teardown();
          rotators.splice(i, 1);
        }
      }
    }));

    doc.addEventListener('shopify:block:select', guard(function (event) {
      var target = event && event.target;
      var slide = closest(target, '[data-rr-ann-slide]');
      var rotator = slide && rotatorFor(slide);
      if (!rotator) return;
      var index = rotator.slides.indexOf(slide);
      if (index < 0) return;
      rotator.selected = true;
      if (index !== rotator.index) rotator.go(index);
      rotator.hold();
    }));

    doc.addEventListener('shopify:block:deselect', guard(function (event) {
      var rotator = rotatorFor(event && event.target);
      if (!rotator) return;
      rotator.selected = false;
      rotator.release();
    }));
  } catch (err) {
    if (window.console && typeof window.console.warn === 'function') {
      window.console.warn('rr-announce: ' + (err && err.message ? err.message : err));
    }
  }
})();
